/**
 * Training Profile Utilities
 * Shared helpers for evaluating DB-driven tutorial quest condition ASTs and
 * resolving profile-controlled simulator visibility/locking behavior.
 *
 * @module trainingProfileUtils
 */

import {
  calculateEnvelopeLoss,
  calculateRadiatorHeatTransfer,
  calculateRoomVolume,
  calculateVentilationLoss,
  calculateVolumetricFlow,
} from './utils';

/**
 * Operators supported by backend condition AST validation.
 */
export const CONDITION_OPERATORS = Object.freeze({
  equals: 'equals',
  notEquals: 'notEquals',
  greaterThan: 'greaterThan',
  greaterThanOrEqual: 'greaterThanOrEqual',
  lessThan: 'lessThan',
  lessThanOrEqual: 'lessThanOrEqual',
  between: 'between',
  in: 'in',
  contains: 'contains',
  approximately: 'approximately',
  greaterThanOrEqualFieldMultiplier: 'greaterThanOrEqualFieldMultiplier',
  lessThanOrEqualFieldMultiplier: 'lessThanOrEqualFieldMultiplier',
});

/**
 * Evaluates a tutorial condition AST safely against live simulation metrics.
 * This function never evaluates JavaScript source strings; it only interprets
 * a small allowlisted AST shape produced by the profile editor/backend.
 *
 * @param {Object|null} ast - Condition AST with group nodes ({ combinator, rules }) or rule nodes
 * @param {Object} metrics - Live simulation state metrics
 * @returns {{passed: boolean, results: Array<{label: string, passed: boolean}>}}
 */
export const evaluateConditionAst = (ast, metrics = {}) => {
  if (!ast || typeof ast !== 'object') {
    return { passed: false, results: [] };
  }

  const results = [];
  const passed = evaluateNode(ast, metrics, results);
  return { passed, results };
};

const evaluateNode = (node, metrics, results) => {
  if (!node || typeof node !== 'object') return false;

  if (Array.isArray(node.rules)) {
    const combinator = String(node.combinator || node.condition || 'and').toLowerCase();
    const childResults = node.rules.map(rule => evaluateNode(rule, metrics, results));
    return combinator === 'or'
      ? childResults.some(Boolean)
      : childResults.every(Boolean);
  }

  const passed = evaluateRule(node, metrics);
  results.push({
    label: node.label || buildConditionLabel(node),
    passed,
  });
  return passed;
};

const evaluateRule = (rule, metrics) => {
  const actual = metrics?.[rule.field];
  const operator = rule.operator;

  switch (operator) {
    case CONDITION_OPERATORS.equals:
      return actual === rule.value;
    case CONDITION_OPERATORS.notEquals:
      return actual !== rule.value;
    case CONDITION_OPERATORS.greaterThan:
      return toNumber(actual) > toNumber(rule.value);
    case CONDITION_OPERATORS.greaterThanOrEqual:
      return toNumber(actual) >= toNumber(rule.value);
    case CONDITION_OPERATORS.lessThan:
      return toNumber(actual) < toNumber(rule.value);
    case CONDITION_OPERATORS.lessThanOrEqual:
      return toNumber(actual) <= toNumber(rule.value);
    case CONDITION_OPERATORS.between:
      return evaluateBetween(actual, rule.value);
    case CONDITION_OPERATORS.in:
      return Array.isArray(rule.value) && rule.value.includes(actual);
    case CONDITION_OPERATORS.contains:
      return String(actual ?? '').toLowerCase().includes(String(rule.value ?? '').toLowerCase());
    case CONDITION_OPERATORS.approximately:
      return Math.abs(toNumber(actual) - toNumber(rule.value)) <= toNumber(rule.tolerance ?? 0.5);
    case CONDITION_OPERATORS.greaterThanOrEqualFieldMultiplier:
      return toNumber(actual) >= toNumber(metrics?.[rule.compareField]) * toNumber(rule.multiplier ?? 1);
    case CONDITION_OPERATORS.lessThanOrEqualFieldMultiplier:
      return toNumber(actual) <= toNumber(metrics?.[rule.compareField]) * toNumber(rule.multiplier ?? 1);
    default:
      return false;
  }
};

const evaluateBetween = (actual, range) => {
  if (!Array.isArray(range) || range.length !== 2) return false;
  const value = toNumber(actual);
  const min = toNumber(range[0]);
  const max = toNumber(range[1]);
  return value >= Math.min(min, max) && value <= Math.max(min, max);
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : NaN;
};

const buildConditionLabel = (rule) => {
  if (!rule?.field || !rule?.operator) return 'Condition';
  if (rule.operator === CONDITION_OPERATORS.between && Array.isArray(rule.value)) {
    return `${rule.field} between ${rule.value[0]} and ${rule.value[1]}`;
  }
  if (rule.operator?.includes('FieldMultiplier')) {
    return `${rule.field} ${rule.operator} ${rule.compareField} × ${rule.multiplier ?? 1}`;
  }
  return `${rule.field} ${rule.operator} ${rule.value}`;
};

const toFiniteNumber = (value, fallback = NaN) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const calculateConditionQNet = (state) => {
  const outdoorTemperature = toFiniteNumber(state.outdoorTemperature);
  const desiredTemperature = toFiniteNumber(state.desiredTemperature);
  const supplyTemperature = toFiniteNumber(state.supplyTemperature);
  const systemPressure = toFiniteNumber(state.systemPressure);
  const radiatorCount = toFiniteNumber(state.radiatorCount);
  const uValue = toFiniteNumber(state.uValue);
  const totalArea = toFiniteNumber(state.totalArea);
  const roofArea = toFiniteNumber(state.roofArea);
  const wallArea = toFiniteNumber(state.wallArea);
  const airChangeRate = toFiniteNumber(state.airChangeRate);
  const valvePosition = toFiniteNumber(state.valvePosition);

  if (
    !Number.isFinite(outdoorTemperature)
    || !Number.isFinite(desiredTemperature)
    || !Number.isFinite(supplyTemperature)
    || !Number.isFinite(systemPressure)
    || !Number.isFinite(radiatorCount)
    || !Number.isFinite(uValue)
    || !Number.isFinite(totalArea)
    || !Number.isFinite(roofArea)
    || !Number.isFinite(wallArea)
    || !Number.isFinite(airChangeRate)
    || !Number.isFinite(valvePosition)
    || !state.radiatorType
  ) {
    return NaN;
  }

  const volumetricFlow = calculateVolumetricFlow(systemPressure, 5, 15);
  const designPowerResult = calculateRadiatorHeatTransfer({
    supplyTemperature,
    roomTemperature: desiredTemperature,
    volumetricFlow,
    valvePosition,
    thermostatOpening: 1,
    radiatorCount,
    radiatorType: state.radiatorType,
  });
  const envelopeLossAtTarget = calculateEnvelopeLoss(
    uValue,
    totalArea,
    desiredTemperature,
    outdoorTemperature,
    roofArea
  );
  const roomVolume = calculateRoomVolume(roofArea, wallArea);
  const ventilationLossAtTarget = calculateVentilationLoss(
    desiredTemperature,
    outdoorTemperature,
    roomVolume,
    airChangeRate
  );

  return (designPowerResult?.heatOutput || 0) - envelopeLossAtTarget.total - ventilationLossAtTarget;
};

/**
 * Creates the metrics object used by DB-driven tutorial conditions.
 * @param {Object} state - Simulation state values
 * @returns {Object} Condition metrics
 */
export const createSimulationMetrics = (state) => ({
  ...state,
  qNet: calculateConditionQNet(state),
  totalHeatLoss: (Number(state.envelopeLoss) || 0) + (Number(state.ventilationLoss) || 0),
});

const TRAINING_PROFILE_CONDITION_FIELD_KEYS = Object.freeze([
  'isSimulationRunning',
  'outdoorTemperature',
  'selectedRoomType',
  'desiredTemperature',
  'supplyTemperature',
  'heatOutput',
  'envelopeLoss',
  'ventilationLoss',
  'airChangeRate',
  'currentCOP',
  'currentTemperature',
  'valvePosition',
  'thermostatLevel',
  'radiatorType',
  'radiatorCount',
  'systemPressure',
  'uValue',
  'totalArea',
  'roofArea',
  'wallArea',
  'windowArea',
  'selectedCity',
]);

/**
 * Builds the runtime metrics snapshot used by DB-driven tutorial conditions.
 *
 * @param {Object} state - Simulation state values
 * @returns {Object} Condition metrics snapshot
 */
export const buildTrainingProfileConditionMetrics = (state = {}) => {
  const metricsState = TRAINING_PROFILE_CONDITION_FIELD_KEYS.reduce((accumulator, key) => {
    if (Object.prototype.hasOwnProperty.call(state, key)) {
      accumulator[key] = state[key];
    }

    return accumulator;
  }, {});

  return createSimulationMetrics(metricsState);
};

/**
 * Returns true when a profile control config marks a key as hidden.
 * @param {Object|null} profile - Training profile detail/public DTO
 * @param {string} key - Registry key
 * @returns {boolean}
 */
export const isProfileControlHidden = (profile, key) => {
  const control = profile?.uiConfiguration?.controls?.[key] || profile?.uiConfiguration?.[key];
  return control?.visible === false || control?.hidden === true || control?.displayMode === 'hidden';
};

/**
 * Returns true when a profile fixed-values config locks a simulation value.
 * @param {Object|null} profile - Training profile detail/public DTO
 * @param {string} key - Simulation value key
 * @returns {boolean}
 */
export const isProfileValueLocked = (profile, key) => {
  const fixedValue = profile?.fixedValues?.[key];
  const control = profile?.uiConfiguration?.controls?.[key]
    || profile?.uiConfiguration?.controls?.[`controlPanel.${key}`]
    || profile?.uiConfiguration?.[key]
    || profile?.uiConfiguration?.[`controlPanel.${key}`];

  if (fixedValue !== undefined && fixedValue !== null) {
    if (typeof fixedValue === 'object') {
      return fixedValue.locked !== false && fixedValue.editable !== true;
    }

    return true;
  }

  if (control?.locked === true || control?.editable === false) {
    return true;
  }

  return false;
};

/**
 * Resolves the fixed value for a simulation key from a training profile.
 * @param {Object|null} profile - Training profile detail/public DTO
 * @param {string} key - Simulation value key
 * @returns {*} The configured fixed value, or undefined
 */
export const getProfileFixedValue = (profile, key) => {
  const fixedValue = profile?.fixedValues?.[key];
  if (fixedValue && typeof fixedValue === 'object' && Object.prototype.hasOwnProperty.call(fixedValue, 'value')) {
    return fixedValue.value;
  }
  return fixedValue;
};

/**
 * Normalizes a learner-entered step answer for comparison.
 * Matching stays trim-based exact text except that numeric decimal comma and dot
 * formats are treated as equivalent.
 *
 * @param {string|null|undefined} value - Raw learner or trainer value
 * @returns {{ text: string, numeric: number|null }} Normalized text plus optional numeric value
 */
export const normalizeTrainingProfileExpectedValue = (value) => {
  const text = typeof value === 'string' ? value.trim() : '';
  const numericPattern = /^[-+]?\d+(?:[.,]\d+)?$/;

  if (!numericPattern.test(text)) {
    return { text, numeric: null };
  }

  const numeric = Number(text.replace(',', '.'));
  return {
    text,
    numeric: Number.isFinite(numeric) ? numeric : null,
  };
};

/**
 * Compares a learner-entered step answer against the configured expected value.
 *
 * @param {string|null|undefined} expectedValue - Trainer-authored expected value
 * @param {string|null|undefined} learnerValue - Learner-entered value
 * @returns {boolean} True when the values match according to the training profile rule set
 */
export const doesTrainingProfileExpectedValueMatch = (expectedValue, learnerValue) => {
  const expected = normalizeTrainingProfileExpectedValue(expectedValue);
  const learner = normalizeTrainingProfileExpectedValue(learnerValue);

  if (expected.numeric !== null && learner.numeric !== null) {
    return expected.numeric === learner.numeric;
  }

  return expected.text === learner.text;
};

const normalizeTrainingProfileBooleanAnswer = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === 'yes') {
      return true;
    }
    if (normalized === 'false' || normalized === 'no') {
      return false;
    }
  }

  return null;
};

const normalizeTrainingProfileInteger = (value) => {
  if (Number.isInteger(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
  }

  return null;
};

const normalizeTrainingProfileCorrectOptionIndexes = (answerCheck) => {
  const indexes = Array.isArray(answerCheck?.correctOptionIndexes)
    ? answerCheck.correctOptionIndexes
      .map((value) => normalizeTrainingProfileInteger(value))
      .filter((value) => value !== null)
    : [];

  if (indexes.length === 0) {
    const legacyIndex = normalizeTrainingProfileInteger(answerCheck?.correctOptionIndex);
    if (legacyIndex !== null) {
      indexes.push(legacyIndex);
    }
  }

  return [...new Set(indexes)].sort((left, right) => left - right);
};

const normalizeTrainingProfileAnswerCheck = (answerCheck, index) => {
  if (!answerCheck || typeof answerCheck !== 'object') {
    return null;
  }

  const name = typeof answerCheck.name === 'string' ? answerCheck.name.trim() : '';
  const value = typeof answerCheck.value === 'string' ? answerCheck.value.trim() : '';
  const question = typeof answerCheck.question === 'string' ? answerCheck.question.trim() : '';
  const correct = typeof answerCheck.correct === 'boolean' ? answerCheck.correct : null;
  const options = Array.isArray(answerCheck.options)
    ? answerCheck.options.map((option) => String(option || '').trim()).filter(Boolean)
    : [];
  const correctOptionIndexes = normalizeTrainingProfileCorrectOptionIndexes(answerCheck);
  const orderIndex = Number.isFinite(answerCheck.orderIndex) ? answerCheck.orderIndex : index;

  if (name.length > 0 && value.length > 0 && question.length === 0 && correct === null) {
    return {
      id: `expected-value-${orderIndex}`,
      type: 'expectedValue',
      orderIndex,
      name,
      value,
      question: null,
      correct: null,
      justInTimeRichContent: typeof answerCheck.justInTimeRichContent === 'string' ? answerCheck.justInTimeRichContent : null,
    };
  }

  if (question.length > 0 && correct !== null && name.length === 0 && value.length === 0) {
    return {
      id: `yes-no-${orderIndex}`,
      type: 'yesNoQuestion',
      orderIndex,
      name: null,
      value: null,
      question,
      correct,
      justInTimeRichContent: typeof answerCheck.justInTimeRichContent === 'string' ? answerCheck.justInTimeRichContent : null,
    };
  }

  if (question.length > 0 && options.length >= 2 && correctOptionIndexes.length > 0 && correct === null && name.length === 0 && value.length === 0) {
    return {
      id: `multiple-choice-${orderIndex}`,
      type: 'multipleChoice',
      orderIndex,
      name: null,
      value: null,
      question,
      correct: null,
      justInTimeRichContent: typeof answerCheck.justInTimeRichContent === 'string' ? answerCheck.justInTimeRichContent : null,
      options,
      correctOptionIndexes,
      correctOptionIndex: correctOptionIndexes.length === 1 ? correctOptionIndexes[0] : null,
    };
  }

  return null;
};

/**
 * Returns the ordered answer checks configured for a step.
 * Supports both the new answerChecks collection and the legacy expected-value fields.
 *
 * @param {Object|null} step - Normalized tutorial step
 * @returns {Array<Object>} Ordered normalized answer checks
 */
export const getTrainingProfileStepAnswerChecks = (step) => {
  if (Array.isArray(step?.answerChecks)) {
    return step.answerChecks
      .map((answerCheck, index) => normalizeTrainingProfileAnswerCheck(answerCheck, index))
      .filter(Boolean)
      .sort((left, right) => left.orderIndex - right.orderIndex);
  }

  if (
    typeof step?.expectedValueName === 'string'
    && step.expectedValueName.trim().length > 0
    && typeof step?.expectedValue === 'string'
    && step.expectedValue.trim().length > 0
  ) {
    return [{
      id: 'expected-value-0',
      type: 'expectedValue',
      orderIndex: 0,
      name: step.expectedValueName.trim(),
      value: step.expectedValue.trim(),
      question: null,
      correct: null,
    }];
  }

  return [];
};

/**
 * Compares one learner answer against one configured training-profile answer check.
 *
 * @param {Object|null} answerCheck - Normalized answer check
 * @param {string|boolean|number|null|undefined} learnerValue - Learner-entered value
 * @returns {boolean} True when the learner answer matches the configured check
 */
export const doesTrainingProfileAnswerCheckMatch = (answerCheck, learnerValue) => {
  if (!answerCheck || typeof answerCheck !== 'object') {
    return false;
  }

  if (answerCheck.type === 'yesNoQuestion') {
    return normalizeTrainingProfileBooleanAnswer(learnerValue) === answerCheck.correct;
  }

  if (answerCheck.type === 'multipleChoice') {
    const expectedIndexes = normalizeTrainingProfileCorrectOptionIndexes(answerCheck);
    const learnerIndexes = Array.isArray(learnerValue)
      ? learnerValue
        .map((value) => normalizeTrainingProfileInteger(value))
        .filter((value) => value !== null)
      : (() => {
          const normalizedValue = normalizeTrainingProfileInteger(learnerValue);
          return normalizedValue === null ? [] : [normalizedValue];
        })();

    if (expectedIndexes.length === 0 || learnerIndexes.length === 0) {
      return false;
    }

    const normalizedLearnerIndexes = [...new Set(learnerIndexes)].sort((left, right) => left - right);
    return normalizedLearnerIndexes.length === expectedIndexes.length
      && normalizedLearnerIndexes.every((value, index) => value === expectedIndexes[index]);
  }

  return doesTrainingProfileExpectedValueMatch(answerCheck.value, learnerValue);
};

/**
 * Returns true when a step contains one or more trainer-authored answer checks.
 *
 * @param {Object|null} step - Normalized tutorial step
 * @returns {boolean} Whether the step contains an answer challenge
 */
export const hasTrainingProfileStepAnswerChecks = (step) => getTrainingProfileStepAnswerChecks(step).length > 0;

/**
 * Returns true when a step contains trainer-authored answer-check metadata.
 *
 * @param {Object|null} step - Normalized tutorial step
 * @returns {boolean} Whether the step contains an answer challenge
 */
export const hasTrainingProfileStepExpectedValue = (step) => hasTrainingProfileStepAnswerChecks(step);
